import {
  CustomMappingAssociatedValueType,
  CustomMappingOption,
  CustomMappingType,
  ICustomMapping,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { ManualTransactionType } from '@principle-theorem/principle-core';
import {
  IManualTransactionType,
  TransactionProvider,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  INamedDocument,
  WithRef,
  XSLXImporterExporter,
  asyncForEach,
  multiMap,
  multiSwitchMap,
  snapshot,
  toNamedDocument,
} from '@principle-theorem/shared';
import { sortBy, upperFirst } from 'lodash';
import { Observable, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import {
  IBaseTransactionTypeOption,
  TransactionTypeToXSLX,
} from './transaction-type-to-xlsx';
import { XSLXToTransactionType } from './xlsx-to-transaction-type';

export interface IBaseMigrationPaymentTypeSource {
  id: string;
  name: string;
}

export const TRANSACTION_TYPE_CUSTOM_MAPPING_TYPE = 'transactionTypeMapping';

export const TRANSACTION_TYPE_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Payment Type Mapping',
    description: `Used to map Payment Types to Principle Transaction Types. Any unmapped payment types will be assigned to a Manual Transaction type.`,
    type: TRANSACTION_TYPE_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
});

const migrationTransactionProviderOptions = [
  TransactionProvider.AccountCredit,
  TransactionProvider.Cash,
  TransactionProvider.Discount,
  TransactionProvider.Manual,
  TransactionProvider.Stripe,
];

export abstract class BaseTransactionTypeMappingHandler<
  PaymentTypeSource extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<PaymentTypeSource>,
> extends BaseCustomMappingHandler<
  IManualTransactionType,
  TransactionProvider,
  INamedDocument<IManualTransactionType>
> {
  customMapping = TRANSACTION_TYPE_MAPPING;
  abstract translateFn: (
    record: PaymentTypeSource['data']
  ) => IBaseMigrationPaymentTypeSource;
  abstract sourceEntity: SourceEntity;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const referralSource = this.translateFn(record);
        return {
          label: referralSource.name,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getSelectionListOptions(
    _migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return of([
      {
        value: '',
        description: '',
        hasAssociatedValue: false,
        associatedValueType: CustomMappingAssociatedValueType.String,
        associatedValueDescription: '',
      },
      ...migrationTransactionProviderOptions.map((provider) => ({
        value: provider,
        description: upperFirst(provider),
        hasAssociatedValue:
          provider === TransactionProvider.Manual ? true : false,
        associatedValueType: CustomMappingAssociatedValueType.NamedDocument,
        associatedValueDescription: 'Select an Manual Transaction Subtype',
      })),
    ]).toPromise();
  }

  getAssociatedValueOptions$(
    migration: IPracticeMigration,
    destinationValue: { value: TransactionProvider }
  ): Observable<INamedDocument<IManualTransactionType>[]> {
    if (destinationValue.value !== TransactionProvider.Manual) {
      return of([]);
    }

    return ManualTransactionType.all$(migration.configuration.brand).pipe(
      multiMap(toNamedDocument)
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, paymentTypes, translators } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      paymentTypes,
      translators
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, paymentTypes, translators } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      paymentTypes,
      translators
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToTransactionType()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const paymentTypes = await this._getPaymentOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const mapTo = item.mapTo;
      const id = item.sourceId;
      const label = item.sourceName;
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.value === id
      )?.value;
      if (!value || !item.mapTo) {
        return;
      }

      const paymentType = paymentTypes.find(
        (configuration) => configuration.label === mapTo
      );

      if (!paymentType) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find payment type for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          destinationValue: paymentType.destinationValue,
          associatedValue: paymentType.associatedValue,
          sourceIdentifier: value,
          sourceLabel: label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    paymentTypes: ICustomMappingSourceOption[];
    translators: TransactionTypeToXSLX;
  }> {
    const fileName = this.getFileName();
    const paymentTypes = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translators = new TransactionTypeToXSLX(
      await this._getPaymentOptions(migration),
      await this.getRecords(translationMap)
    );
    return { fileName, paymentTypes, translators };
  }

  private async _getPaymentOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseTransactionTypeOption[]> {
    return snapshot(
      from(this.getSelectionListOptions(migration)).pipe(
        multiSwitchMap((option) => {
          if (!option.hasAssociatedValue) {
            return of([
              {
                destinationValue: {
                  value: option.value as TransactionProvider,
                },
                label: option.description,
              },
            ]);
          }

          return this.getAssociatedValueOptions$(migration, {
            value: option.value as TransactionProvider,
          }).pipe(
            map((associatedValues) => {
              return associatedValues.map((associatedValue) => ({
                destinationValue: {
                  value: option.value as TransactionProvider,
                },
                associatedValue,
                label: `${option.description} - ${associatedValue.name}`,
              }));
            })
          );
        }),
        map((options) => options.flat())
      )
    );
  }
}
